import { TruckIcon } from "@heroicons/react/24/outline";
import Label from "components/Label/Label";
import NcInputNumber from "components/NcInputNumber";
import Prices from "components/Prices";
import { SEND_PRICE } from "constants/data";
import {
StockWarning,
WarningIcon
} from "containers/ProductDetailPage/GroupedDetailPage";
import { SelectSendOption } from "containers/Refunds/RefundsPage";
import { useCart } from "context/cart/cart.context";
import { calculateFreeShippingTotal } from "context/cart/cart.utils";
import { Student,WooCommerceError } from "data/data";
import { CartItem,SEND_TYPE } from "data/types";
import { useCallback,useEffect,useMemo,useRef,useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { Link,useNavigate } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Checkbox from "shared/Checkbox/Checkbox";
import Input from "shared/Input/Input";
import { useUpdateBillingMutation } from "store/api/customersApi";
import { useCreateOrderMutation,useValidateCouponMutation } from "store/api/ordersApi";
import { setUser } from "store/slices";
import { useCollege,useUser } from "store/utils/hooks";
import getEuro from "utils/getEuro";
import { normalizeAttrName } from "utils/normalizeAttr";
import { post } from "utils/post";
import ContactInfo from "./ContactInfo";
import PaymentMethod from "./PaymentMethod";
import ShippingAddress from "./ShippingAddress";
import Students from "./Students";

type FormValues = {
  first_name: string;
  last_name: string;
  address_1: string;
  city: string;
  state: string;
  postcode: number;
  country: string;
  email: string;
  phone: string;
};

type CouponScope = "products" | "shipping";

type ValidatedCoupon = {
  id: number;
  name: string;
  value: string | number;
  type: string;
  is_active: boolean;
  scope?: CouponScope;
  scope_label?: string;
};

const getCouponScope = (coupon: ValidatedCoupon): CouponScope =>
  coupon.scope === "shipping" ? "shipping" : "products";

const getScopedCouponDiscount = (coupon: ValidatedCoupon | null, base: number) => {
  if (!coupon || base <= 0) return 0;

  const value = Number(coupon.value);
  if (!Number.isFinite(value) || value <= 0) return 0;

  const rawDiscount =
    coupon.type === "percent" || coupon.type === "percentage"
      ? base * (value / 100)
      : value;

  return Math.min(rawDiscount, base);
};

const CheckoutPage = () => {
  const [buy, res] = useCreateOrderMutation();
  const [update, resUpdate] = useUpdateBillingMutation();
  const [validateCoupon] = useValidateCouponMutation();
  const {
    items,
    total,
    resetCart,
    addItemToCart,
    removeItemFromCart,
    clearItemFromCart,
  } = useCart();
  const user = useUser();
  const [students, setStudents] = useState<Student[]>([]);
  const [differentBillAddress, setDifferentBillAddress] = useState(false)
  const [student, setStudent] = useState<number>(0);
  const navigate = useNavigate();
  const college = useCollege();
  const dispatch = useDispatch();
  const [to, setTo] = useState("colegio");
  const [paymentMethod, setPaymentMethod] = useState<"Card" | "Bizum">("Card");
  const [paymentMethodActive, setPaymentMethodActive] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [productOrderCouponData, setProductOrderCouponData] = useState<ValidatedCoupon | null>(null);
  const [shippingCouponData, setShippingCouponData] = useState<ValidatedCoupon | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const contactInfoRef = useRef<HTMLDivElement>(null);
  const shippingAddressRef = useRef<HTMLDivElement>(null);
  const studentsRef = useRef<HTMLDivElement>(null);
  const billAddressRef = useRef<HTMLDivElement>(null)
  const paymentMethodRef = useRef<HTMLDivElement>(null);
  const limitSellPrice = Number(college.limit_sell_price || 0)
  // const alumno = getStudent(user);
  // const totalItems = items.reduce((acc, curr) => {
  //   acc += curr.quantity;
  //   return acc;
  // }, 0);
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm<FormValues>({
    defaultValues: useMemo(() => {
      return user || {};
    }, [user]),
  });
  useEffect(() => {
    setStudents(user?.students || []);
  }, [user]);
  useEffect(() => {
    if (res.isError) {
      toast.error((res.error as WooCommerceError).data.message, {
        duration: 10000,
      });
    } else if (res.isSuccess) {
      resetCart();
      if (!res?.data?.data?.redsys) {
        navigate("/ok");
      } else {
        const redsys = res?.data?.data?.redsys
        post(redsys?.form_action, redsys)
      }
      // window.location.href = res.data.payment_url.replace(
      //   /pay_for_order=true&/g,
      //   ""
      // );
    }
  }, [res]);
  const handleScrollToEl = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth" });
      }, 80);
    }
  };

  const hasFamily = user?.big_family;
  const familyDiscount = college.family_discount
    ? total * (college.family_discount / 100)
    : 0;
  const freeShippingTotal = useMemo(() => calculateFreeShippingTotal(items), [items]);
  const isFreeSend = useMemo(() => {
    return Boolean(college?.free_cart_amount && freeShippingTotal >= college?.free_cart_amount);
  }, [college, freeShippingTotal]);

  const hasPromotionalItems = useMemo(
    () => items.some((item) => Boolean(item.in_promotion)),
    [items]
  );

  useEffect(() => {
    if (hasPromotionalItems) {
      setTo("direccion");
    }
  }, [hasPromotionalItems]);

  const sentPrice = hasPromotionalItems
    ? SEND_PRICE
    : to === "colegio"
      ? 0
      : isFreeSend
        ? 0
        : SEND_PRICE;
  useEffect(() => {
    if (shippingCouponData && sentPrice <= 0) {
      setShippingCouponData(null);
    }
  }, [shippingCouponData, sentPrice]);
  const handleApplyCoupon = useCallback(async (codeOverride?: string) => {
    const codeSource = typeof codeOverride === "string" ? codeOverride : couponCode;
    const codeToApply = codeSource.trim();

    if (!codeToApply) {
      setCouponError("Por favor, introduce un código de cupón");
      return;
    }

    setCouponLoading(true);
    setCouponError("");

    try {
      const response = await validateCoupon({ code: codeToApply, school_id: college.id });
      const { data, error } = response;

      if (error) {
        setCouponError(error?.data?.message || "Error al validar el cupón");
        return;
      }

      if (data?.status && data?.data) {
        const coupon = data.data as ValidatedCoupon;
        const scope = getCouponScope(coupon);

        if (scope === "shipping" && sentPrice <= 0) {
          setCouponError("El envío ya es gratis; este cupón solo aplica si hay coste de envío.");
          return;
        }

        if (scope === "shipping") {
          setShippingCouponData(coupon);
        } else {
          setProductOrderCouponData(coupon);
        }

        setCouponCode("");
        setCouponError("");
        toast.success(scope === "shipping" ? "Cupón de envío aplicado" : "Cupón de productos aplicado");
      } else {
        setCouponError(data?.message || "Cupón no válido");
      }
    } catch (error: any) {
      setCouponError(error?.data?.message || "Error al validar el cupón");
    } finally {
      setCouponLoading(false);
    }
  }, [couponCode, validateCoupon, college.id, sentPrice]);
  const originalItemsTotal = useMemo(
    () => items.reduce((acc, item) => acc + Number(item.price || 0) * item.quantity, 0),
    [items]
  );
  const productCouponsDiscountTotal = Math.max(originalItemsTotal - total, 0);
  const itemsWithProductCoupon = useMemo(
    () => items.filter((item) => item.coupon_id || item.coupon_metadata?.code).length,
    [items]
  );

  const productOrderCouponDiscount = getScopedCouponDiscount(productOrderCouponData, total);
  const hasProductOrderCoupon = !!productOrderCouponData;
  const hasShippingCoupon = !!shippingCouponData;
  const hasFreeShippingCoupon = hasShippingCoupon && sentPrice > 0;
  const finalSendPrice = hasFreeShippingCoupon ? 0 : sentPrice;
  const isOrderSendFree =
    hasFreeShippingCoupon || (!hasPromotionalItems && (to === "colegio" || Boolean(isFreeSend)));

  async function onSubmit(data: any) {
    const newOrder = {
      send_type:
        hasPromotionalItems || to !== "colegio"
          ? SEND_TYPE.DIRECTION
          : SEND_TYPE.COLLEGE,
      is_send_free: isOrderSendFree,
      send_price: finalSendPrice,
      discount: hasFamily ? college.family_discount : 0,
      school_id: college.id,
      student,
      is_same_address: to === 'colegio' || !differentBillAddress,
      payment_method: paymentMethod === 'Bizum' ? 'z' : 'C',
      coupon_id: productOrderCouponData?.id || null,
      coupon_metadata: productOrderCouponData || null,
      shipping_coupon_id: hasFreeShippingCoupon ? shippingCouponData?.id || null : null,
      products: items.map((it) => ({
        amount: it.quantity,
        product_id: it.id,
        parent_id: it.parent_id,
        pivot_parent_id: it.pivot_parent_id,
        coupon_id: it.coupon_id || null,
        coupon_metadata: it.coupon_metadata || null,
      })),
    };
    delete data.schools;
    update({
      ...data,
      students: students.map((it) => {
        if (typeof it.id === "string") {
          delete it.id;
        }
        return it;
      }),
    }).then((res) => {
      if (res.error) {
        toast.error((res.error as WooCommerceError).data.message, {
          duration: 10000,
        });
      } else {
        dispatch(setUser(res.data.data));
        buy(newOrder);
      }
    });
  }
  const [tabActive, setTabActive] = useState<
    "ContactInfo" | "ShippingAddress" | "Students" | "BillAddress" | "PaymentMethod"
  >("Students");

  const renderProduct = (item: CartItem, index: number) => {
    const {
      parent_id: id,
      image,
      price,
      name,
      itemTotal,
      attributes,
      quantity,
      stock_quantity,
      limit_store,
      maxCartAmount,
      coupon_metadata,
    } = item;
    const originalLineTotal = Number(price || 0) * quantity;
    const hasProductCouponOnItem =
      Boolean(coupon_metadata?.code) && itemTotal < originalLineTotal;

    return (
      <div key={index} className="relative flex py-7 first:pt-0 last:pb-0">
        <div className="relative h-36 w-24 sm:w-28 flex-shrink-0 overflow-hidden rounded-xl bg-slate-100">
          <img
            src={image}
            alt={name}
            className="h-full w-full object-contain object-center"
          />
          <Link
            to={`/product-detail?id=${id}`}
            className="absolute inset-0"
          ></Link>
        </div>

        <div className="ml-3 sm:ml-6 flex flex-1 flex-col">
          <div>
            <div className="flex justify-between ">
              <div className="flex-[1.5] ">
                <h3 className="text-base font-semibold">
                  <Link to={`/product-detail?id=${item.id}`}>{name}</Link>
                </h3>
                <div className="mt-1.5 sm:mt-2.5 flex text-sm text-slate-600 dark:text-slate-300">
                  {Object.keys(attributes).sort().map(
                    (it: string, idx: number) => {
                      return (
                        <>
                          <span className="capitalize">
                            {normalizeAttrName(it)}: {attributes[it]}
                          </span>
                          {Object.values(attributes).length - 1 !== idx && (
                            <span className="mx-2 border-l border-slate-200 dark:border-slate-700 h-4"></span>
                          )}
                        </>
                      );
                    }
                  )}
                </div>
                <div>
                  {stock_quantity < quantity && (
                    <p className="text-red-400 text-xs">
                      En fabricación, el producto se enviará cuando las
                      cantidades vuelvan a estar disponibles.
                    </p>
                  )}
                  {limit_store && (
                    <p className="text-red-400 text-xs">
                      Este producto está limitado a una compra por estudiante.
                    </p>
                  )}
                  {coupon_metadata?.code && (
                    <p className="text-green-700 text-xs mt-1">
                      Cupón aplicado a esta prenda: {coupon_metadata.code}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex-1 sm:flex justify-end">
                <Prices
                  className="mt-0.5"
                  contentClass="py-1 px-2 md:py-1.5 md:px-2.5 text-base font-semibold"
                  price={hasProductCouponOnItem ? originalLineTotal : itemTotal}
                  refund_price={hasProductCouponOnItem ? itemTotal : undefined}
                />
              </div>
            </div>
          </div>

          <div className="flex mt-auto pt-4 items-end justify-between text-sm">
            <div className="hidden sm:block text-center relative">
              <NcInputNumber
                onIncrement={() => addItemToCart(item, 1)}
                onDecrement={() => removeItemFromCart(item.id)}
                defaultValue={quantity}
                className="relative z-10"
                max={maxCartAmount}
              />
            </div>

            <a
              href="##"
              className="relative z-10 flex items-center mt-3 font-medium text-primary-6000 hover:text-primary-500 text-sm "
              onClick={() => clearItemFromCart(item.id)}
            >
              <span>Eliminar</span>
            </a>
          </div>
        </div>
      </div>
    );
  };

  const renderLeft = () => {
    return (
      <div className="space-y-8">
        <div ref={contactInfoRef} className="scroll-mt-24">
          <ContactInfo
            students={students}
            setStudents={setStudents}
            register={register}
            errors={errors}
            selectedStudentIdx={student}
            setSelectedStudentIdx={setStudent}
            isActive={tabActive === "ContactInfo"}
            onOpenActive={() => {
              setTabActive("ContactInfo");
              handleScrollToEl(contactInfoRef);
            }}
            onCloseActive={() => setTabActive("Students")}
            getValues={getValues}
            setValue={setValue}
          />
        </div>

        <div ref={studentsRef} className="scroll-mt-24">
          <Students
            isActive
            student={student}
            setStudent={setStudent}
            setStudents={setStudents}
            students={students}
          />
        </div>

        <div ref={shippingAddressRef} className="scroll-mt-24">
          <ShippingAddress
            title={'Dirección de facturación'}
            register={register}
            errors={errors}
            isActive={tabActive === "ShippingAddress"}
            onOpenActive={() => {
              setTabActive("ShippingAddress");
              handleScrollToEl(shippingAddressRef);
            }}
            onCloseActive={() => {
              setTabActive("Students");
              handleScrollToEl(shippingAddressRef);
            }}
          />
        </div>

        {to !== 'colegio' && <div>
          <Checkbox onChange={() => setDifferentBillAddress(prev => !prev)} name="" checked={!differentBillAddress} label="La dirección de facturación y envío es la misma"></Checkbox>
        </div>}

        {differentBillAddress && <div ref={billAddressRef} className="scroll-mt-24">
          <ShippingAddress
            icon={<TruckIcon className="w-6 h-6 text-slate-700 dark:text-slate-400 mt-0.5" />}
            title={'Dirección de envío'}
            register={register}
            errors={errors}
            prefixFields="shipping_"
            isActive={tabActive === "BillAddress"}
            onOpenActive={() => {
              setTabActive("BillAddress");
              handleScrollToEl(shippingAddressRef);
            }}
            onCloseActive={() => {
              setTabActive("Students");
              handleScrollToEl(studentsRef);
            }}
          />
        </div>}


        <div ref={paymentMethodRef} className="scroll-mt-24">
          <PaymentMethod
            selected={paymentMethod}
            onChange={setPaymentMethod}
            isActive={paymentMethodActive}
            onOpenActive={() => {
              setPaymentMethodActive(true);
              handleScrollToEl(paymentMethodRef);
            }}
            onCloseActive={() => setPaymentMethodActive(false)}
          />
        </div>
        <SelectSendOption
          collegeLabel={`Envío al colegio GRATIS`}
          addressSendLabel={`Envío a la dirección de facturación ${
            hasFreeShippingCoupon || (!hasPromotionalItems && isFreeSend)
              ? "GRATIS"
              : getEuro(SEND_PRICE)
          }`}
          setSend={(send) => {
            setTo(send);
          }}
          send={to}
          hideCollegeOption={hasPromotionalItems}
          disabled={hasPromotionalItems}
        />
      </div>
    );
  };

  return (
    <div className="nc-CheckoutPage">
      <main className="container py-16 lg:pb-28 lg:pt-20 ">
        <div className="mb-16">
          <h2 className="block text-2xl sm:text-3xl lg:text-4xl font-semibold ">
            Datos del pago
          </h2>
          <div className="block mt-3 sm:mt-5 text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-400">
            <Link to={"/#"} className="">
              Inicio
            </Link>
            <span className="text-xs mx-1 sm:mx-1.5">/</span>
            <span className="underline">Datos del pago</span>
          </div>
        </div>
        {/* <form onSubmit={handleSubmit(onSubmit)}> */}
        <div className="flex flex-col lg:flex-row">
          <div className="flex-1">{renderLeft()}</div>

          <div className="flex-shrink-0 border-t lg:border-t-0 lg:border-l border-slate-200 dark:border-slate-700 my-10 lg:my-0 lg:mx-10 xl:lg:mx-14 2xl:mx-16 "></div>

          <div className="w-full lg:w-[36%] ">
            <h3 className="text-lg font-semibold">Pedido</h3>
            <div className="mt-8 divide-y divide-slate-200/70 dark:divide-slate-700 ">
              {items.map(renderProduct)}
            </div>

            <div className="mt-10 pt-6 text-sm text-slate-500 dark:text-slate-400 border-t border-slate-200/70 dark:border-slate-700 ">
              {itemsWithProductCoupon > 0 && (
                <div className="mb-5 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
                  Ya tienes descuentos aplicados por producto en {itemsWithProductCoupon} prenda(s).
                  Puedes añadir otro cupón; se aplicará a productos o envío según corresponda.
                </div>
              )}
              {/* Coupon Code Section */}
              <div className="mb-6">
                <Label className="text-sm">Cupón</Label>
                <p className="mt-1 text-xs text-slate-500">
                  Puedes aplicar un cupón de productos y otro de envío.
                </p>
                <div className="flex mt-1.5">
                  <Input
                    sizeClass="h-10 px-4 py-3"
                    className="flex-1"
                    placeholder="Introduce tu código"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value);
                      if (couponError) {
                        setCouponError("");
                      }
                    }}
                    disabled={couponLoading}
                  />
                  <button
                    type="button"
                    onClick={() => handleApplyCoupon()}
                    disabled={couponLoading}
                    className="text-neutral-700 dark:text-neutral-200 border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-100 rounded-2xl px-4 ml-3 font-medium text-sm bg-neutral-200/70 dark:bg-neutral-700 dark:hover:bg-neutral-800 w-24 flex justify-center items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {couponLoading ? "..." : "Aplicar"}
                  </button>
                </div>
                {couponError && (
                  <p className="mt-2 text-sm text-red-500 dark:text-red-400">
                    {couponError}
                  </p>
                )}
                {(productOrderCouponData || shippingCouponData) && (
                  <div className="mt-3 space-y-2 text-sm">
                    {productOrderCouponData && (
                      <div className="flex items-center justify-between gap-3 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-green-700">
                        <span>Cupón de productos: {productOrderCouponData.name}</span>
                        <button
                          type="button"
                          onClick={() => setProductOrderCouponData(null)}
                          className="cursor-pointer text-xs font-semibold underline"
                        >
                          Quitar
                        </button>
                      </div>
                    )}
                    {shippingCouponData && (
                      <div className="flex items-center justify-between gap-3 rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2 text-cyan-700">
                        <span>Cupón de envío: {shippingCouponData.name}</span>
                        <button
                          type="button"
                          onClick={() => setShippingCouponData(null)}
                          className="cursor-pointer text-xs font-semibold underline"
                        >
                          Quitar
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-4 flex justify-between py-2.5">
                <span>Precio</span>
                <span className="font-semibold text-slate-900 dark:text-slate-200">
                  {getEuro(total / 1.21)}
                </span>
              </div>
              {productCouponsDiscountTotal > 0 && (
                <div className="flex justify-between py-2.5">
                  <span>Descuento por productos</span>
                  <span className="font-semibold text-slate-900 dark:text-slate-200">
                    {getEuro(-productCouponsDiscountTotal)}
                  </span>
                </div>
              )}
              <div className="flex justify-between py-2.5">
                <span>IVA</span>
                <span className="font-semibold text-slate-900 dark:text-slate-200">
                  {getEuro(total - total / 1.21)}
                </span>
              </div>
              <div className="flex justify-between py-2.5">
                <span>Envío</span>
                <span className="font-semibold text-slate-900 dark:text-slate-200">
                  {sentPrice === 0 ? (
                    "GRATIS"
                  ) : hasFreeShippingCoupon ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="text-slate-400 line-through">{getEuro(sentPrice)}</span>
                      <span className="text-green-600">GRATIS</span>
                    </span>
                  ) : (
                    getEuro(sentPrice)
                  )}
                </span>
              </div>
              {hasFamily && (
                <div className="flex justify-between py-2.5">
                  <span>Descuento familia</span>
                  <span className="font-semibold text-slate-900 dark:text-slate-200">
                    {getEuro(-familyDiscount)}
                  </span>
                </div>
              )}
              {hasProductOrderCoupon && productOrderCouponDiscount > 0 && (
                <div className="flex justify-between py-2.5">
                  <span>Cupón productos: {productOrderCouponData?.name}</span>
                  <span className="font-semibold text-slate-900 dark:text-slate-200">
                    {getEuro(-productOrderCouponDiscount)}
                  </span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-slate-900 dark:text-slate-200 text-base pt-4">
                <span>Subtotal</span>
                <span>{getEuro(total)}</span>
              </div>
              <div className="flex justify-between font-semibold text-slate-900 dark:text-slate-200 text-base pt-4">
                <span>Total</span>
                <span>
                  {getEuro(
                    total + finalSendPrice - (hasFamily ? familyDiscount || 0 : 0) - productOrderCouponDiscount
                  )}
                </span>
              </div>
            </div>
            {/* {res.isSuccess && res.data.data.redsys && (
              <PaymentForm paymentParams={res.data.data.redsys} />
            )} */}
            <ButtonPrimary
              disabled={items.length === 0 || !students?.[student] || total < limitSellPrice}
              onClick={handleSubmit(onSubmit)}
              loading={res.isSuccess || res.isLoading || resUpdate.isLoading}
              type="submit"
              className="mt-8 w-full"
            >
              Realizar Pago
            </ButtonPrimary>
            {items?.some((it) => it.quantity > it.stock_quantity) && (
              <StockWarning />
            )}
            {/* <Dialog open={res.isSuccess}>
              <DialogContent>
                <div className="p-8 flex flex-col gap-2 items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-10 w-10 mx-auto"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="3"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <h5>Redireccionando...</h5>
                </div>
              </DialogContent>
            </Dialog> */}
            {
              total < limitSellPrice && (
                <div className="mt-5 text-sm text-red-500 dark:text-red-400 flex items-center justify-center">
                  <p className="block relative pl-5">
                    <WarningIcon />
                    El precio minimo de compra es de {getEuro(limitSellPrice)}
                  </p>
                </div>
              )
            }
            {!students?.[student] && (
              <div className="mt-5 text-sm text-red-500 dark:text-red-400 flex items-center justify-center">
                <p className="block relative pl-5">
                  <WarningIcon />
                  Debe agregar al menos un estudiante
                </p>
              </div>
            )}
            {/* Errors details */}
            {
              Object.values(errors).length !== 0 && (
                <div className="mt-5 text-sm text-red-500 dark:text-red-400">

                  {Object.values(errors).map((it, idx) => {
                    return <p className="block relative pl-5" key={idx}>- {it.message}</p>
                  })}
                </div>
              )
            }
            {items.length === 0 && !res.isSuccess && (
              <div className="mt-5 text-sm text-red-500 dark:text-red-400 flex items-center justify-center">
                <p className="block relative pl-5">
                  <svg
                    className="w-4 h-4 absolute -left-1 top-0.5"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 8V13"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M11.9945 16H12.0035"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Agregue algún producto para realizar la compra
                </p>
              </div>
            )}
          </div>
        </div>
        {/* </form> */}
      </main>
    </div>
  );
};

export default CheckoutPage;
